import React from "react";
import { FiBarChart2, FiClock, FiDownload, FiMail, FiMessageSquare, FiPhone, FiShare2, FiUser } from "react-icons/fi";
import useTimeFormat, { getLocalTimeZone } from "../../../hook/useTimeFormat";

const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString() : "0";
};

const textValue = (value) => {
  const text = String(value || "").trim();
  return text || "—";
};

const platformLabel = (value) => {
  return textValue(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const GalleryMediaStats = ({ loading, media, title }) => {
  const { formateLocalTime } = useTimeFormat();
  const timestamp = media?.capture_date_time || media?.captured_at || media?.taken_at || media?.created_at;
  const timestampLabel = media?.capture_date_time || media?.captured_at || media?.taken_at ? "Captured" : "Created";
  const sharedBy = Array.isArray(media?.shared_by) ? media.shared_by : [];
  const channels = [
    ["Email", media?.email_shares_count, <FiMail />],
    ["Facebook", media?.facebook_shares_count, <FiShare2 />],
    ["Instagram", media?.instagram_shares_count, <FiShare2 />],
    ["LinkedIn", media?.linkedin_shares_count, <FiShare2 />],
    ["SMS", media?.sms_shares_count, <FiMessageSquare />],
  ];

  return (
    <div className="gallery-media-stats">
      <header>
        <span><FiBarChart2 /> Media insights</span>
        <h2>{title}</h2>
      </header>

      {loading ? (
        <div className="gallery-media-stats-loading"><span className="spinner-border" role="status" aria-label="Loading media details" /></div>
      ) : (
        <>
          <div className="gallery-media-stat-summary">
            <article><FiShare2 /><span>Total shares</span><strong>{numberValue(media?.shares_count)}</strong></article>
            <article><FiDownload /><span>Downloads</span><strong>{numberValue(media?.download_shares_count)}</strong></article>
          </div>

          <div className="gallery-media-channel-list">
            <h3>Sharing activity</h3>
            {channels.map(([label, value, icon]) => (
              <div key={label}><span>{icon}{label}</span><strong>{numberValue(value)}</strong></div>
            ))}
          </div>

          {sharedBy.length > 0 ? (
            <section className="gallery-media-shared-by">
              <header>
                <div>
                  <h3>Shared by</h3>
                  <p>People matched to this media&apos;s sharing activity.</p>
                </div>
                <span>{sharedBy.length}</span>
              </header>
              <div className="gallery-media-share-card-list" role="list">
                {sharedBy.map((share, index) => (
                  <article className="gallery-media-share-card" role="listitem" key={share?.share_id || `${share?.sharing_platform || "share"}-${index}`}>
                    <header>
                      <span className="gallery-media-share-avatar"><FiUser /></span>
                      <div>
                        <small>Name</small>
                        <strong>{textValue(share?.name)}</strong>
                      </div>
                    </header>

                    <div className="gallery-media-share-contact-grid">
                      <div>
                        <span><FiPhone /> Mobile</span>
                        <strong>{textValue(share?.mobile)}</strong>
                      </div>
                      <div>
                        <span><FiMail /> Email</span>
                        <strong>{textValue(share?.email)}</strong>
                      </div>
                    </div>

                    <footer>
                      <div>
                        <small>Sharing platform</small>
                        <span className="gallery-media-platform-chip">{platformLabel(share?.sharing_platform)}</span>
                      </div>
                      <div>
                        <small><FiClock /> Shared at</small>
                        <strong>{formateLocalTime(share?.shared_at)}</strong>
                      </div>
                    </footer>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <footer>
            <FiClock />
            <div><span>{timestampLabel}</span><strong>{formateLocalTime(timestamp)}</strong><small>Local time · {getLocalTimeZone()}</small></div>
          </footer>
        </>
      )}
    </div>
  );
};

export default GalleryMediaStats;
